import type { PlanName } from "@/app/billing/types";
import type { Plan } from "./plans";
import { plans } from "./plans";

export function getPlanColorClasses(color: string) {
  // Map of color names to their corresponding Tailwind classes
  const colorMap = {
    primary: {
      hoverRing: "hover:ring-1 hover:ring-primary",
      hoverBorder: "hover:border-primary",
      ring: "ring-primary",
      badgeText: "text-brand-text",
      badgeBorder: "border-brand-text/40",
    },
    "yellow-400": {
      hoverRing: "hover:ring-1 hover:ring-yellow-400",
      hoverBorder: "hover:border-yellow-400",
      ring: "ring-yellow-400",
      badgeText: "text-yellow-400",
      badgeBorder: "border-yellow-400/40",
    },
    "green-400": {
      hoverRing: "hover:ring-1 dark:hover:ring-green-400 hover:ring-green-600",
      hoverBorder: "dark:hover:border-green-400 hover:border-green-600",
      ring: "dark:ring-green-400 ring-green-600",
      badgeText: "dark:text-green-400 text-green-600",
      badgeBorder: "dark:border-green-400/40 border-green-600/40",
    },
    "pink-500": {
      hoverRing: "hover:ring-1 hover:ring-pink-500",
      hoverBorder: "hover:border-pink-500",
      ring: "ring-pink-500",
      badgeText: "text-pink-500",
      badgeBorder: "border-pink-500/40",
    },
  } as const;

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  return colorMap[color as keyof typeof colorMap] ?? colorMap.primary;
}

/**
 * Get plan object from external plan ID
 */
export function getPlanFromId(planId: string): Plan | null {
  return plans.find((p) => p.id === planId) ?? null;
}

/**
 * Determine if a plan is the next logical upgrade for the current plan
 */
export function isNextPlan(
  currentPlanId: string | null,
  targetPlanId: string
): boolean {
  if (!currentPlanId) {
    return false;
  }

  // Plan upgrade hierarchy based on external IDs
  const upgradeMap: Record<string, string[]> = {
    free: ["paygo_monthly"],
    paygo_monthly: ["enterprise"],
    enterprise: [], // No upgrades from enterprise
  };

  const possibleUpgrades = upgradeMap[currentPlanId] || [];
  return possibleUpgrades.includes(targetPlanId);
}

/**
 * Get the plan tier name from external plan ID
 */
export function getPlanTierFromId(planId: string): PlanName {
  if (planId === "free") {
    return "free";
  }
  if (planId.startsWith("growth_")) {
    return "growth";
  }
  if (planId === "enterprise") {
    return "enterprise";
  }
  return "enterprise"; // fallback
}

/**
 * Determine if a plan change is an upgrade or downgrade
 */
export function isPlanUpgrade(
  currentPlanId: string | null,
  targetPlanId: string
): boolean {
  if (!currentPlanId) {
    return false; // a missing planId indicates they are on a custom enterprise plan
  }

  // Plan hierarchy (lower index = lower tier)
  const planHierarchy = [
    "free",
    "starter_monthly", // legacy id
    "starter_annual", // legacy id
    "growth_monthly", // legacy id
    "growth_annual", // legacy id
    "paygo_monthly",
    "enterprise",
  ];

  const currentIndex = planHierarchy.indexOf(currentPlanId);
  const targetIndex = planHierarchy.indexOf(targetPlanId);

  // If either plan is not in hierarchy, assume it's an upgrade if target is higher
  if (currentIndex === -1 || targetIndex === -1) {
    return targetIndex > currentIndex;
  }

  return targetIndex > currentIndex;
}
